import { useState } from 'preact/hooks';
import { useAuth } from '../../context/AuthContext.js';
import { useFetch } from '../../hooks/useFetch.js';
import { ApiError, api } from '../../services/api.js';
import { Button } from './Button.js';
import { Spinner } from './Spinner.js';
import { lookupFailedMessage } from './resultWording.js';

interface LocationItem {
  _id: string;
  name: string;
}

/**
 * 330 (US2, FR-003, contracts/ui.md) — the one-time campus choice.
 *
 * Shown by `AuthGuard` in place of any protected screen while the account has no campus, after the
 * phone step. The condition is the missing field, so new sign-ups, Google sign-ups and every existing
 * customer are asked the same way, with nothing run against the database. Choosing locks it: only staff
 * change a campus afterwards, which the confirmation says before anything is saved.
 *
 * Like `PhoneRequired`, it always offers Sign out: a required screen with no way out is a locked
 * account. While staff impersonate a customer, it offers no choice at all (the server refuses one too);
 * staff set a campus from the admin.
 */
export function CampusRequired() {
  const { logout, refreshUser, isImpersonating, exitImpersonation } = useAuth();
  const locations = useFetch<{ items: LocationItem[] }>(isImpersonating ? null : '/locations');
  const [picked, setPicked] = useState<LocationItem | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function confirm() {
    if (!picked) return;
    setSaving(true);
    setError('');
    try {
      await api.put('/users/me/campus', { location_id: picked._id });
      await refreshUser();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      if (code === 'CAMPUS_LOCKED') {
        // Already chosen (another device got there first): re-read, and the guard lets them in.
        await refreshUser();
        return;
      }
      setError(
        code === 'CAMPUS_NOT_AVAILABLE'
          ? 'That campus is no longer available. Choose another.'
          : 'Could not save your campus. Please try again.',
      );
      if (code === 'CAMPUS_NOT_AVAILABLE') {
        setConfirming(false);
        setPicked(null);
        locations.retry();
      }
      setSaving(false);
    }
  }

  const signOut = (
    <button
      type="button"
      data-testid="campus-sign-out"
      onClick={() => logout()}
      className="mt-6 w-full text-sm text-gray-400 underline"
    >
      Sign out
    </button>
  );

  if (isImpersonating) {
    return (
      <div data-testid="campus-required" className="page-fill bg-bg px-4 pt-10">
        <h1 className="text-xl font-bold text-text mb-2">Choose your campus</h1>
        <p className="text-sm text-gray-400 mb-6">
          Only the customer can choose their campus. Staff can set it from the customer’s page in
          the admin.
        </p>
        <Button size="lg" className="w-full" onClick={() => exitImpersonation()}>
          Stop impersonating
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="campus-required" className="page-fill bg-bg px-4 pt-10">
      <h1 className="text-xl font-bold text-text mb-2">Choose your campus</h1>
      <p className="text-sm text-gray-400 mb-6">You’ll see and book the cars at your campus.</p>

      {error && (
        <p role="alert" className="mb-4 text-sm text-red-400">
          {error}
        </p>
      )}

      {confirming && picked ? (
        <div data-testid="campus-confirm" className="flex flex-col gap-4">
          <p className="rounded-lg bg-surface-card px-4 py-3 text-text">
            Your campus will be {picked.name}. You can't change it yourself later — only Carii can.
          </p>
          <Button size="lg" className="w-full" loading={saving} onClick={confirm}>
            Confirm
          </Button>
          <Button
            variant="ghost"
            size="lg"
            className="w-full"
            disabled={saving}
            onClick={() => setConfirming(false)}
          >
            Back
          </Button>
        </div>
      ) : locations.status === 'ok' ? (
        <fieldset className="flex flex-col gap-3">
          <legend className="sr-only">Campus</legend>
          {(locations.data?.items ?? []).map((loc) => (
            <label
              key={loc._id}
              data-testid={`campus-option-${loc._id}`}
              className={[
                'flex min-h-12 items-center gap-3 rounded-lg bg-surface-card px-4 py-3 text-text',
                picked?._id === loc._id ? 'ring-2 ring-primary' : '',
              ].join(' ')}
            >
              <input
                type="radio"
                name="campus"
                value={loc._id}
                checked={picked?._id === loc._id}
                onChange={() => setPicked(loc)}
                className="h-5 w-5 accent-primary"
              />
              <span>{loc.name}</span>
            </label>
          ))}
          <Button
            size="lg"
            className="mt-2 w-full"
            disabled={!picked}
            onClick={() => setConfirming(true)}
          >
            Continue
          </Button>
        </fieldset>
      ) : locations.status === 'loading' || locations.status === 'idle' ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : (
        <p role="alert" className="text-sm text-red-400">
          {lookupFailedMessage('The campuses', 'Please try again later.')}
        </p>
      )}

      {signOut}
    </div>
  );
}
